/**
 * Copyright 2026 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import React from "react";
import PropTypes from "prop-types";
import T from "i18n-react/dist/i18n-react";
import { Document, Page, Text, View, Image, pdf } from "@react-pdf/renderer";
import { createStyles, createRowStyles } from "./styles";
import {
  buildRows,
  formatDate,
  formatAddress,
  formatVenueName,
  getThemeFontFamily,
  fmtBalance,
  getOrderTotal
} from "./helpers";
import { FieldRow } from "./components/field-row";
import { PdfTableRow } from "./components/pdf-table-row";
import { ReconciliationBlock } from "./components/reconciliation-block";

export { buildRows };

// logoSrc/theme are left to the consumer on purpose: this component is
// shared across apps with different branding/typefaces, so no logo image or
// font file ships with uicore. fontFamily is read off theme.typography and
// falls back to a react-pdf built-in (no Font.register needed) when absent
// OR when not registered via Font.register in the consuming app — react-pdf
// throws on an unregistered family rather than falling back itself, so
// getThemeFontFamily checks Font.getRegisteredFontFamilies() before using it.
export const OrderPdf = ({ order, summit, logoSrc, theme }) => {
  if (!order) throw new Error("OrderPdf: order is required");
  if (!summit) throw new Error("OrderPdf: summit is required");

  const fontFamily = getThemeFontFamily(theme);
  const styles = createStyles(fontFamily);
  const rowStyles = createRowStyles(styles);
  const rows = buildRows(order, summit);
  const mainLocation =
    summit.main_locations?.[0] ??
    summit.locations?.find((location) => location.is_main);

  const {
    cancelled_total: cancelledTotal = 0,
    refunds_total: refundsTotal = 0,
    retained = 0,
    credited_to_payment_method: credited = 0
  } = order;

  const total = getOrderTotal(order);

  const clientName =
    order.client?.contact_name || order.purchased_by_full_name || "";
  const clientCompany = order.client?.company_name || "";
  const clientAddress = formatAddress(order.address) || T.translate("general.not_available");

  return (
    <Document>
      <Page size="A4" orientation="portrait" style={styles.page}>
        {/* Row 1: Logo | Invoice title + Order / Date */}
        <View style={styles.headerRow} wrap={false}>
          <View style={styles.cellLeft}>
            {logoSrc ? <Image src={logoSrc} style={styles.logo} /> : null}
          </View>
          <View style={styles.cellRight}>
            <Text style={styles.invoiceTitle}>
              {T.translate("order_invoice_pdf.invoice_title")}
            </Text>
            <FieldRow
              styles={styles}
              label={T.translate("order_invoice_pdf.order")}
              value={order.number}
            />
            <FieldRow
              styles={styles}
              label={T.translate("order_invoice_pdf.date")}
              value={order.purchased_date ? formatDate(
                order.purchased_date,
                summit.time_zone_id,
                "YYYY/MM/DD hh:mm a"
              ) : T.translate("order_invoice_pdf.pending")}
            />
            <FieldRow
              styles={styles}
              label={T.translate("order_invoice_pdf.payment_method")}
              value={order.payment_method ?? ""}
            />
            <FieldRow
              styles={styles}
              label={T.translate("order_invoice_pdf.status")}
              value={order.status ?? ""}
            />
            <FieldRow
              styles={styles}
              label={T.translate("order_invoice_pdf.payment_date")}
              value={order.created ? formatDate(
                order.created,
                "LOC",
                "YYYY/MM/DD hh:mm a"
              ) : ""}
              noBorder
            />
          </View>
        </View>

        {/* Row 2: Client + Address | Event + Venue + Address */}
        <View style={styles.headerRow} wrap={false}>
          <View style={styles.cellLeft}>
            <FieldRow
              styles={styles}
              label={T.translate("order_invoice_pdf.client")}
              value={`${clientCompany}\n${clientName}`}
            />
            <FieldRow
              styles={styles}
              label={T.translate("order_invoice_pdf.address")}
              value={clientAddress}
              noBorder
            />
          </View>
          <View style={styles.cellRight}>
            <FieldRow
              styles={styles}
              label={T.translate("order_invoice_pdf.event")}
              value={summit.name ?? ""}
            />
            <FieldRow
              styles={styles}
              label={T.translate("order_invoice_pdf.venue")}
              value={formatVenueName(mainLocation) ?? ""}
            />
            <FieldRow
              styles={styles}
              label={T.translate("order_invoice_pdf.address")}
              value={formatAddress(mainLocation) ?? ""}
              noBorder
            />
          </View>
        </View>

        {/* Table */}
        <View style={styles.tableWrapper}>
          <View style={styles.table}>
            <View style={styles.thRow} fixed>
              <Text style={[styles.thText, styles.colCode]}>
                {T.translate("sponsor_order_grid.code")}
              </Text>
              <Text style={[styles.thText, styles.colType]}>
                {T.translate("sponsor_order_grid.type")}
              </Text>
              <Text style={[styles.thText, styles.colDesc]}>
                {T.translate("sponsor_order_grid.details")}
              </Text>
              <Text style={[styles.thText, styles.colPrice]}>
                {T.translate("sponsor_order_grid.amount")}
              </Text>
              <Text style={[styles.thText, styles.colBalance]}>
                {T.translate("sponsor_order_grid.balance")}
              </Text>
            </View>

            {rows.map((row) => (
              <PdfTableRow
                key={row.rowKey}
                row={row}
                styles={styles}
                rowStyles={rowStyles}
              />
            ))}

            <ReconciliationBlock
              styles={styles}
              cancelledTotal={cancelledTotal}
              refundsTotal={refundsTotal}
              retained={retained}
              credited={credited}
            />

            <View style={styles.tdAmountDue}>
              <Text style={styles.tdAmountDueLabel}>
                {T.translate("sponsor_order_grid.amount_due")}
              </Text>
              <View style={{ width: "18%" }} />
              <Text
                style={[
                  styles.tdAmountDueValue,
                  {
                    color: (total ?? 0) > 0 ? "#e65100" : "#1b5e20"
                  }
                ]}
              >
                {fmtBalance(total ?? 0)}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

OrderPdf.propTypes = {
  order: PropTypes.object.isRequired,
  summit: PropTypes.object.isRequired,
  logoSrc: PropTypes.string,
  theme: PropTypes.object
};

// Revoking a blob URL immediately after use has been reported to produce
// empty/failed downloads on Safari and some Firefox versions, so both
// generateInvoicePDF and previewPDF defer the revoke by this long instead.
const REVOKE_DELAY_MS = 60_000;

export const generateInvoicePDF = async (
  order,
  summit,
  { logoSrc, theme } = {}
) => {
  try {
    const blob = await pdf(
      <OrderPdf
        order={order}
        summit={summit}
        logoSrc={logoSrc}
        theme={theme}
      />
    ).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `invoice-${order.number}.pdf`
      .toLowerCase()
      .replace(/\s+/g, "-");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  } catch (error) {
    console.error("Error generating invoice PDF:", error);
    throw error;
  }
};

export const previewPDF = async (order, summit, { logoSrc, theme } = {}) => {
  // Opened synchronously, before the await below, so the tab is still tied
  // to the originating click — otherwise Safari's popup blocker kills it.
  // Can't pass "noopener" (or "noreferrer", which implies it) here: that
  // makes window.open return null, and we need the reference to set
  // .location.href once the blob URL is ready.
  const newTab = window.open("", "_blank");

  try {
    const blob = await pdf(
      <OrderPdf order={order} summit={summit} logoSrc={logoSrc} theme={theme} />
    ).toBlob();
    const url = URL.createObjectURL(blob);

    if (newTab) {
      newTab.location.href = url;
      // Revoke after the new tab has had a chance to load the PDF.
      setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
      return;
    }

    // Popup blocked: fall back to a download so the click isn't silently
    // swallowed.
    URL.revokeObjectURL(url);
    return generateInvoicePDF(order, summit, { logoSrc, theme });
  } catch (error) {
    console.error("Error generating invoice PDF preview:", error);
    if (newTab) newTab.close();
    throw error;
  }
};
